class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    
    // Add abort controller for request cancellation
    this.abortController = null;

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    console.log('fetching data');
    
    // Cancel any previous request to prevent race conditions
    if (FacetFiltersForm.currentAbortController) {
      FacetFiltersForm.currentAbortController.abort();
    }
    
    // Create new abort controller for this request
    FacetFiltersForm.currentAbortController = new AbortController();
    
    // Add timeout for better error handling
    const timeoutId = setTimeout(() => {
      if (FacetFiltersForm.currentAbortController) {
        FacetFiltersForm.currentAbortController.abort();
      }
    }, 30000); // 30 second timeout
    
    fetch(url, { signal: FacetFiltersForm.currentAbortController.signal })
      .then((response) => {
        clearTimeout(timeoutId);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        // Validate response is not empty
        if (!responseText || responseText.trim().length === 0) {
          throw new Error('Empty response received from server');
        }
        
        const html = responseText;
        console.log('html:', html);
        
        // Implement cache size limit to prevent memory leaks
        const MAX_CACHE_SIZE = 10;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        
        // Remove oldest entries if cache exceeds limit
        if (FacetFiltersForm.filterData.length > MAX_CACHE_SIZE) {
          FacetFiltersForm.filterData = FacetFiltersForm.filterData.slice(-MAX_CACHE_SIZE);
        }
        
        // Wrap rendering in try-catch for better error handling
        try {
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
          if (typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger(html.innerHTML);
          }
        } catch (renderError) {
          console.error('Error rendering filters:', renderError);
          // Fallback: reload the page if rendering fails
          window.location.reload();
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          console.log('Request was cancelled to prevent race condition');
        } else {
          console.error('Fetch error:', error);
          // Show user-friendly error message
          FacetFiltersForm.showErrorMessage('Failed to load filters. Please try again.');
        }
      })
      .finally(() => {
        // Clear the abort controller reference
        if (FacetFiltersForm.currentAbortController) {
          FacetFiltersForm.currentAbortController = null;
        }
        // Hide loading spinners even if there was an error
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      });
  }

  static showErrorMessage(message) {
    // Create or update error message element
    let errorElement = document.getElementById('facet-error-message');
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.id = 'facet-error-message';
      errorElement.className = 'facet-error-message';
      errorElement.style.cssText = `
        background: #f8d7da;
        color: #721c24;
        padding: 12px;
        margin: 16px 0;
        border: 1px solid #f5c6cb;
        border-radius: 4px;
        text-align: center;
      `;
      
      const facetContainer = document.querySelector('.facets-container');
      if (facetContainer) {
        facetContainer.insertBefore(errorElement, facetContainer.firstChild);
      }
    }
    
    errorElement.textContent = message;
    errorElement.style.display = 'block';
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    }, 5000);
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    
    // Improved mobile detection and form state synchronization
    const isMobile = event.target.closest('form')?.id === 'FacetFiltersFormMobile';
    const isSpecialCheckbox = event.srcElement?.className === 'mobile-facets__checkbox';
    
    if (isSpecialCheckbox) {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      
      sortFilterForms.forEach((form) => {
        // Ensure we're collecting the right forms based on context
        if (!isMobile) {
          // Desktop: collect desktop forms only
          if (['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm', 'FacetFiltersPillsForm'].includes(form.id)) {
            forms.push(this.createSearchParams(form));
          }
        } else {
          // Mobile: collect mobile forms only
          if (form.id === 'FacetFiltersFormMobile') {
            forms.push(this.createSearchParams(form));
          }
        }
      });
      
      // Deduplicate parameters to prevent conflicts
      const uniqueParams = new URLSearchParams();
      forms.forEach(formParams => {
        const params = new URLSearchParams(formParams);
        for (const [key, value] of params) {
          if (!uniqueParams.has(key)) {
            uniqueParams.set(key, value);
          }
        }
      });
      
      this.onSubmitForm(uniqueParams.toString(), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    
    // Selecting range inputs using data attributes
    this.minRangeInput = this.querySelector('[data-range-type="min"]');
    this.maxRangeInput = this.querySelector('[data-range-type="max"]');
    
    // Selecting corresponding text inputs using data attributes
    this.minTextInput = this.querySelector('[data-input-type="min"]');
    this.maxTextInput = this.querySelector('[data-input-type="max"]');
    
    // Debug: Log selected elements to ensure they are being selected correctly
    console.log('Min Range Input:', this.minRangeInput);
    console.log('Max Range Input:', this.maxRangeInput);
    console.log('Min Text Input:', this.minTextInput);
    console.log('Max Text Input:', this.maxTextInput);
    
    // Bind event listeners for the range inputs
    this.minRangeInput.addEventListener('input', this.onRangeInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.onRangeInput.bind(this));
    
    // Debug: Log when the event listeners are added
    console.log('Event listeners added for range inputs');
    
    // Debounced version of the form submission handler
    this.debouncedOnSubmit = debounce(this.onRangeSubmit.bind(this), 800);

    this.minRangeInput.addEventListener('change', this.debouncedOnSubmit);
    this.maxRangeInput.addEventListener('change', this.debouncedOnSubmit);
  }

  // Update the text inputs as the range sliders move
  onRangeInput(event) {
    const minValue = this.minRangeInput.value;
    const maxValue = this.maxRangeInput.value;

    // Debug: Log the values when the range input changes
    console.log('Range Input Event - Min Value:', minValue);
    console.log('Range Input Event - Max Value:', maxValue);

    this.minTextInput.value = (minValue / 100).toFixed(2); // Assuming price is in cents
    this.maxTextInput.value = (maxValue / 100).toFixed(2);

    // Debug: Log updated text input values
    console.log('Updated Min Text Input:', this.minTextInput.value);
    console.log('Updated Max Text Input:', this.maxTextInput.value);
  }

  // Submit the form to refresh products after the user stops adjusting the range
  onRangeSubmit(event) {
    const form = event.target.closest('form');

    // Debug: Log form data
    console.log('Submitting form with new range values', new FormData(form));

    const searchParams = new URLSearchParams(new FormData(form)).toString();
    FacetFiltersForm.renderPage(searchParams, event); // Using your existing FacetFiltersForm logic
  }
}

// Helper function to debounce execution
function debounce(fn, delay) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), delay);
    
    // Debug: Log debounce trigger
    console.log('Debounce triggered');
  };
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
